import { useQuery } from '@apollo/client';

import QueryCollection from 'bundles/campaigns/queries/QueryCollection.graphql';
import type {
  CollectionFragment,
  QueryCollectionQuery as QueryCollectionData,
  QueryCollectionQueryVariables,
} from 'bundles/campaigns/queries/__generated__/QueryCollection';
import { getProductTypeFromTypename } from 'bundles/collections-carousel/constants';
import type { CarouselCollection, CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';

export type UseCollectionOptions = {
  queryCollectionId: string;
  enterpriseProgramSlug?: string;
  additionalInput?: {
    preset_collection_id?: string;
    enterprise_program_id?: string;
  };
  limit?: number;
  skip?: boolean;
};

export const normalizeCollection = (
  content?: CollectionFragment,
  enterpriseProgramSlug?: string
): CarouselCollection | null => {
  if (!content?.entities?.elements) {
    return null;
  }

  const normalizedEntities: CollectionEntity[] = content.entities.elements.flatMap((entity) => {
    if (entity?.__typename === 'ProductCard_ProductCard') {
      const { productTypeAttributes, marketingProductType } = entity;

      if ('canonical' in productTypeAttributes && productTypeAttributes.canonical) {
        const canonical = productTypeAttributes.canonical;

        let productCardImageUrl;
        if (canonical.__typename === 'Specialization_Specialization') productCardImageUrl = canonical.cardImageUrl;
        if (canonical.__typename === 'Course_Course') productCardImageUrl = canonical.promoPhotoUrl;

        let productUrl;
        let enterpriseProductUrl;
        if (canonical.__typename === 'Specialization_Specialization') productUrl = canonical.urlPath;
        if (canonical.__typename === 'Course_Course') productUrl = '/learn/' + canonical.slug;
        if (enterpriseProgramSlug) enterpriseProductUrl = '/programs/' + enterpriseProgramSlug + productUrl;

        return [
          {
            id: canonical.id,
            name: canonical.name,
            slug: canonical.slug,
            imageUrl: productCardImageUrl || '',
            isCostFree: 'isFree' in productTypeAttributes ? productTypeAttributes.isFree : false,
            coBrandingEnabled: true,
            isPathwayContent:
              'isPathwayContent' in productTypeAttributes ? productTypeAttributes.isPathwayContent : false,
            partners:
              canonical.partners?.map((partner) => ({
                id: partner.id,
                name: partner.name,
                logo: partner.logo || '',
              })) || [],
            partnerLogos: canonical.partners?.map((partner) => partner.squareLogo || '') || [], // displayed on product card
            link: enterpriseProductUrl || productUrl || '',
            productType: getProductTypeFromTypename(marketingProductType),
          },
        ] as CollectionEntity[];
      }
    }

    return [];
  });

  const collection: CarouselCollection = {
    collectionId: content.id,
    title: content.description,
    entities: normalizedEntities,
  };

  return collection;
};

/**
 * This hook calls QueryCollection to fetch collection data (by collection ID) and normalizes the response
 * into a CarouselCollection object (making it possible to render the collection via cds-labs' CollectionsModule).
 *
 * Supports consumer and enterprise, as well as preset collections.
 *
 * @param {string} queryCollectionId - (Required) ID of the collection to fetch
 * @param {string} [enterpriseProgramSlug] - (Enterprise only) Used to build program-specific product URLs
 * @param {JSON} additionalInput - (Optional) Additional input for the query
 * @property {string} [preset_collection_id] - (Optional) For preset collections
 * @property {string} [enterprise_program_id] - (Enterprise only) Get program-specific collection data
 * @param {number} [limit] - (Optional) Limit the number of entities returned [default: 50]
 * @param {boolean} skip - (Optional) Skip the query [default: false if queryCollectionId is provided]
 */
function useCollection({
  queryCollectionId,
  enterpriseProgramSlug,
  additionalInput,
  limit = 50, // usually defaults to 20, but some collections have up to 50 entities
  skip = false,
}: UseCollectionOptions) {
  const { loading, data, error } = useQuery<QueryCollectionData, QueryCollectionQueryVariables>(QueryCollection, {
    variables: {
      queryCollectionId,
      additionalInput,
      limit,
    },
    skip: skip || !queryCollectionId,
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all' as const,
    context: { clientName: 'gatewayGql' },
    ssr: false, // Intentionally client side to prevent crawling of information
  });

  if (skip) {
    return {
      skipped: true,
      loading: false,
      collection: null,
      error: null,
    };
  }

  return {
    skipped: false,
    loading,
    collection: normalizeCollection(data?.Collection?.queryCollection, enterpriseProgramSlug),
    error,
  };
}

export default useCollection;
